import shortid from 'shortid';
import _, { assign, isArray } from 'lodash';
import * as actionTypes from './umoActionTypes';
import { selectWorkspace } from './workspacePreview';
import { retrieveUMOs, deselectAll } from './umoGallery';
import piction, { relateUMOsPeer, rateUMO } from '../utils/piction';
import { HighlightSetTag, WorkspaceTag } from '../utils/searchUtils';
import { addError, addIntlError, addIntlInfo } from './messageDialog';
import appConfig from '../appConfig';
import { SESSION_EXPIRED_MESSAGE } from '../constants/sessionStateTypes';
import { sessionExpired } from './session';
import { editWorkspaceFailure, editWorkspaceRequest, editWorkspaceSuccess } from './thesaurus';

export const addToWorkspaceFailure = (requestID, workspaceID, umoList, err) => ({
  type: actionTypes.UMO_ADD_TO_WORKSPACE_FAILURE,
  requestID,
  workspaceID,
  umoList,
  err,
});

export const addToWorkspaceSuccess = (requestID, workspaceID, umoList, searchParams) => ({
  type: actionTypes.UMO_ADD_TO_WORKSPACE_SUCCESS,
  requestID,
  workspaceID,
  umoList,
  searchParams,
});

export const addToWorkspaceRequest = (
  requestID,
  requestPromise,
  workspaceID,
  umoList,
  searchParams
) => ({
  type: actionTypes.UMO_ADD_TO_WORKSPACE_REQUEST,
  requestID,
  requestPromise,
  workspaceID,
  umoList,
  searchParams,
});

// todo: at the moment, when adding/removing multiple umos, if one fails, the whole request
// is seen as having failed. the call itself supports individual fails/success, but right
// now this app doesn't. will need to implement this.

/**
 * Use to add umos to a workspace
 * @param workspaceID
 * @param umoList A list of umo objects. i.e. PictionJS.UMO. If searchParams is given, the items in
 * this list is removed from the search query.
 * @param searchParams Optional search query used to retrieve the UMOs that should be added to the
 * workspace. Can either be SearchParams instance or a search query string e.g. IN_CATEGORY:xxx
 * @param options contins options like isCopy or OrderBy
 */
export const addToWorkspace = (workspaceID, umoList = [], searchParams, options) =>
  function addToWorkspaceDispatch(dispatch, getState) {
    const requestID = shortid.generate();
    const newUMOList = isArray(umoList) ? umoList : umoList.toJS();

    const params = assign(
      searchParams &&
      { SEARCH: _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() },
      options && options.isCopy && { COPY: 'TRUE' },
      options && options.orderBy && { ORDER_BY: options.orderBy },
    );

    const promise = piction.addToLightbox(workspaceID, newUMOList, params)
      .then((res) => {
        dispatch(addToWorkspaceSuccess(requestID, workspaceID, umoList, searchParams));

        // todo: HACK: below is hacky. it's also not tested. it's a quick way to force refresh of
        // the workspace preview panel
        const selectedWorkspaceID = getState().workspacePreview.get('selectedID');
        if (selectedWorkspaceID === workspaceID) {
          const data = getState().workspacePreview.get('selectedData');
          dispatch(selectWorkspace(
            workspaceID,
            data.get('name'),
            data.get('description'),
          ));
        }

        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(addToWorkspaceFailure(requestID, workspaceID, umoList, err));

          // hack to refresh the workspace anyway. this mainly applies to adding multiple items
          // where there is a mixture of failures and successes.
          const selectedWorkspaceID = getState().workspacePreview.get('selectedID');
          if (selectedWorkspaceID === workspaceID) {
            const data = getState().workspacePreview.get('selectedData');
            dispatch(selectWorkspace(
              workspaceID,
              data.get('name'),
              data.get('description'),
            ));
          }
        }
        return err;
      });

    dispatch(addToWorkspaceRequest(requestID, promise, workspaceID, umoList, searchParams));
    return promise;
  };

export const removeFromWorkspaceFailure = (requestID, workspaceID, umoList, err) => ({
  type: actionTypes.UMO_REMOVE_FROM_WORKSPACE_FAILURE,
  requestID,
  workspaceID,
  umoList,
  err,
});

export const removeFromWorkspaceSuccess = (requestID, workspaceID, umoList, searchParams) => ({
  type: actionTypes.UMO_REMOVE_FROM_WORKSPACE_SUCCESS,
  requestID,
  workspaceID,
  umoList,
  searchParams,
});

export const removeFromWorkspaceRequest = (
  requestID,
  requestPromise,
  workspaceID,
  umoList,
  searchParams
) => ({
  type: actionTypes.UMO_REMOVE_FROM_WORKSPACE_REQUEST,
  requestID,
  requestPromise,
  workspaceID,
  umoList,
  searchParams,
});

export const removeFromWorkspace = (workspaceID, umoList = [], searchParams) =>
  function removeFromWorkspaceDispatch(dispatch, getState) {
    const requestID = shortid.generate();
    const newUMOList = isArray(umoList) ? umoList : umoList.toJS();
    let params;

    if (searchParams) {
      params = { SEARCH: _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() };
    }

    const promise = piction.deleteFromLightbox(workspaceID, newUMOList.map(umo => umo.id), params)
    .then((res) => {
      dispatch(removeFromWorkspaceSuccess(requestID, workspaceID, umoList, searchParams));

      const selectedWorkspaceID = getState().workspacePreview.get('selectedID');
      if (selectedWorkspaceID === workspaceID) {
        const data = getState().workspacePreview.get('selectedData');
        dispatch(selectWorkspace(
          workspaceID,
          data.get('name'),
          data.get('description'),
        ));
      }

      // hack to refresh the umo gallery if it's showing the same workspace
      const umoGallerySearchTagList = getState().umoGallery &&
        getState().umoGallery.getIn(['searchParams', 'tagList']);
      if ((umoGallerySearchTagList &&
        umoGallerySearchTagList[0] instanceof WorkspaceTag &&
        umoGallerySearchTagList[0].value === workspaceID) ||
        umoGallerySearchTagList[0] instanceof HighlightSetTag
      ) {
        dispatch(retrieveUMOs());
      }

      return res;
    })
    .catch((err) => {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        console.log(err);
        dispatch(removeFromWorkspaceFailure(requestID, workspaceID, umoList, err));

        // hack to refresh the workspace anyway. this mainly applies to adding multiple items
        // where there is a mixture of failures and successes.
        const selectedWorkspaceID = getState().workspacePreview.get('selectedID');
        if (selectedWorkspaceID === workspaceID) {
          const data = getState().workspacePreview.get('selectedData');
          dispatch(selectWorkspace(
            workspaceID,
            data.get('name'),
            data.get('description'),
          ));
        }

        // hack to refresh the umo gallery if it's showing the same workspace
        const umoGallerySearchTagList = getState().umoGallery &&
          getState().umoGallery.getIn(['searchParams', 'tagList']);
        if ((umoGallerySearchTagList &&
          umoGallerySearchTagList[0] instanceof WorkspaceTag &&
          umoGallerySearchTagList[0].value === workspaceID) ||
          umoGallerySearchTagList[0] instanceof HighlightSetTag
        ) {
          dispatch(retrieveUMOs());
        }
      }
      return err;
    });

    dispatch(removeFromWorkspaceRequest(requestID, promise, workspaceID, umoList, searchParams));
    return promise;
  };

export const moveBetweenWorkspacesFailure =
  (requestID, fromWorkspaceID, toWorkspaceID, umoList, err) => ({
    type: actionTypes.UMO_MOVE_BETWEEN_WORKSPACES_FAILURE,
    requestID,
    fromWorkspaceID,
    toWorkspaceID,
    umoList,
    err,
  });

export const moveBetweenWorkspacesSuccess = (
  requestID,
  fromWorkspaceID,
  toWorkspaceID,
  umoList,
  searchParams) => ({
    type: actionTypes.UMO_MOVE_BETWEEN_WORKSPACES_SUCCESS,
    requestID,
    fromWorkspaceID,
    toWorkspaceID,
    umoList,
    searchParams,
  });

export const moveBetweenWorkspacesRequest = (
  requestID,
  requestPromise,
  fromWorkspaceID,
  toWorkspaceID,
  umoList,
  searchParams
) => ({
  type: actionTypes.UMO_MOVE_BETWEEN_WORKSPACES_REQUEST,
  requestID,
  requestPromise,
  fromWorkspaceID,
  toWorkspaceID,
  umoList,
  searchParams,
});

/**
 * Use when moving umos in the workspace area. can only move within workspace,
 * not between two parent workspaces
 * @param fromWorkspaceID
 * @param toWorkspaceID
 * @param umoList A list of umo objects. i.e. PictionJS.UMO. If searchParams is given, the items in
 * this list is removed from the search query.
 * @param searchParams Optional search query used to retrieve the UMOs that should be added to the
 * workspace. Can either be SearchParams instance or a search query string e.g. IN_CATEGORY:xxx
 * @param isCopy when copying umo instead of moving
 */
export const moveBetweenWorkspaces = (
  fromWorkspaceID,
  toWorkspaceID,
  umoList = [],
  searchParams,
  isCopy) =>
  function moveBetweenWorkspacesDispatch(dispatch, getState) {
    const requestID = shortid.generate();
    const newUMOList = isArray(umoList) ? umoList : umoList.toJS();

    const params = assign(
      searchParams &&
      { SEARCH: _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() },
      isCopy && { COPY: 'TRUE' },
    );

    if (appConfig.inProgressNotifications) {
      dispatch(addIntlInfo('categoryMoveDialog.InProgressMoveMessage'));
    }
    const promise =
      piction.moveBetweenLightboxes(fromWorkspaceID, toWorkspaceID, newUMOList, params)
    .then((res) => {
      dispatch(moveBetweenWorkspacesSuccess(
        requestID, fromWorkspaceID, toWorkspaceID, umoList, searchParams));
      dispatch(addIntlInfo('categoryMoveDialog.successMoveMessage'));

      const data = getState().workspacePreview.get('selectedData');
      const selectedWsID = getState().workspacePreview.get('selectedID');
      dispatch(selectWorkspace(
        selectedWsID,
        data.get('name'),
        data.get('description'),
      ));

      // hack to refresh the umo gallery.
      dispatch(retrieveUMOs());

      return res;
    })
    .catch((err) => {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        const hierarchyErr = 'From and To lightboxes are not within the same WORKSPACE';
        // give specific error message for this error
        if (err.message === hierarchyErr ||
          (err.data && err.data.s && err.data.s.m === hierarchyErr)) {
          dispatch(addIntlError('workspace.dropRestrictionError'));
        } else {
          dispatch(addError(err.message || err.data.s.m));
        }
        dispatch(moveBetweenWorkspacesFailure(
          requestID, fromWorkspaceID, toWorkspaceID, umoList, err));

        // hack to refresh the workspace anyway. this mainly applies to adding multiple items
        // where there is a mixture of failures and successes.
        const data = getState().workspacePreview.get('selectedData');
        dispatch(selectWorkspace(
          getState().workspacePreview.get('selectedID'),
          data.get('name'),
          data.get('description'),
        ));

        // hack to refresh the umo gallery.
        dispatch(retrieveUMOs());
      }
      return err;
    });

    dispatch(moveBetweenWorkspacesRequest(
      requestID, promise, fromWorkspaceID, toWorkspaceID, umoList, searchParams));
    return promise;
  };


export const moveUMOFailure = (requestID, categoryID, umoList, err) => ({
  type: actionTypes.UMO_MOVE_FAILURE,
  requestID,
  categoryID,
  umoList,
  err,
});

export const moveUMOSuccess = (requestID, categoryID, umoList, isCopy) => ({
  type: actionTypes.UMO_MOVE_SUCCESS,
  requestID,
  categoryID,
  umoList,
  isCopy,
});

export const moveUMORequest = (
  requestID,
  requestPromise,
  categoryID,
  umoList
) => ({
  type: actionTypes.UMO_MOVE_REQUEST,
  requestID,
  requestPromise,
  categoryID,
  umoList,
});

export const moveUMO = (umoList = [], categoryID, searchParams, isCopy) =>
  function moveUMODispatch(dispatch) {
    const requestID = shortid.generate();
    const newUMOList = isArray(umoList) ? umoList : umoList.toJS();

    // user can always COPY from a category,
    // but can only MOVE from if he has edit permissions on that category
    const params = assign(
    searchParams &&
      { SEARCH: _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() },
    isCopy && { COPY: 'TRUE' },
    !isCopy && appConfig.restrictEditing && { ENFORCE_CHECKS: 'CHECK_FROM_CTG' },
    );

    if (appConfig.inProgressNotifications) {
      if (isCopy) {
        dispatch(addIntlInfo('categoryMoveDialog.InProgressCopyMessage'));
      } else {
        dispatch(addIntlInfo('categoryMoveDialog.InProgressMoveMessage'));
      }
    }

    const promise = piction.moveUMOs(newUMOList, categoryID, params)
      .then((res) => {
        dispatch(moveUMOSuccess(requestID, categoryID, newUMOList));
        if (isCopy) {
          dispatch(addIntlInfo('categoryMoveDialog.successCopyMessage'));
        } else {
          dispatch(addIntlInfo('categoryMoveDialog.successMoveMessage'));
        }

        // hack to refresh the umo gallery.
        dispatch(retrieveUMOs());
        // refresh selected umos after move
        dispatch(deselectAll());

        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } if (err.message &&
          err.message === 'Session does not have delete, update or alter on originating category') {
          dispatch(moveUMOFailure(requestID, categoryID, newUMOList, err));
          dispatch(addIntlError('navCategoryPanel.noMoveRoleError'));
        } else {
          dispatch(moveUMOFailure(requestID, categoryID, newUMOList, err));
          dispatch(addError(err.message));
        }
        return err;
      });

    dispatch(moveUMORequest(requestID, promise, categoryID, newUMOList));

    return promise;
  };


export const relateUMOsFailure = (requestID, workspaceID) => ({
  type: actionTypes.UMO_RELATE_FAILURE,
  requestID,
  workspaceID,
});

export const relateUMOsSuccess = (requestID, workspaceID) => ({
  type: actionTypes.UMO_RELATE_SUCCESS,
  requestID,
  workspaceID,
});

export const relateUMOsRequest = (
  requestID,
  workspaceID,
) => ({
  type: actionTypes.UMO_RELATE_REQUEST,
  requestID,
  workspaceID,
});

/**
 * Use to relate all umos in a workspace as peer-to-peer
 * @param workspaceID
 */
export const relateUMOs = workspaceID =>
  function relateUMOsDispatch(dispatch, getState) {
    const requestID = shortid.generate();

    relateUMOsPeer({ CATEGORY_ID: workspaceID })
    .then((res) => {
      dispatch(relateUMOsSuccess(requestID, workspaceID));

      // todo: HACK: below is hacky. it's also not tested. it's a quick way to force refresh of
      // the workspace preview panel
      const selectedWorkspaceID = getState().workspacePreview.get('selectedID');
      if (selectedWorkspaceID === workspaceID) {
        const data = getState().workspacePreview.get('selectedData');
        dispatch(selectWorkspace(
          workspaceID,
          data.get('name'),
          data.get('description'),
        ));
      }

      return res;
    })
    .catch((err) => {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(relateUMOsFailure(requestID, workspaceID));

        // hack to refresh the workspace anyway. this mainly applies to adding multiple items
        // where there is a mixture of failures and successes.
        const selectedWorkspaceID = getState().workspacePreview.get('selectedID');
        if (selectedWorkspaceID === workspaceID) {
          const data = getState().workspacePreview.get('selectedData');
          dispatch(selectWorkspace(
            workspaceID,
            data.get('name'),
            data.get('description'),
          ));
        }
      }
      return err;
    });

    dispatch(relateUMOsRequest(requestID, workspaceID));
  };


export const updateUMORatingFailure = (requestID, umoID, newRating) => ({
  type: actionTypes.UMO_RATING_FAILURE,
  requestID,
  umoID,
  newRating,
});

export const updateUMORatingSuccess = (requestID, umoID, newRating) => ({
  type: actionTypes.UMO_RATING_SUCCESS,
  requestID,
  umoID,
  newRating,
});

export const updateUMORatingRequest = (requestID, umoID, newRating) => ({
  type: actionTypes.UMO_RATING_REQUEST,
  requestID,
  umoID,
  newRating,
});

/**
 * Use to set or update rating for umo
 * @param umoID
 */
export const updateUMORating = (umoID, newRating) =>
  function updateUMORatingDispatch(dispatch) {
    const requestID = shortid.generate();

    rateUMO({ UMO_ID: umoID, NEW_VALUE: newRating })
      .then((res) => {
        dispatch(updateUMORatingSuccess(requestID, umoID, newRating));

        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(updateUMORatingFailure(requestID, umoID, newRating));
        }
        return err;
      });

    dispatch(updateUMORatingRequest(requestID, umoID, newRating));
  };

export const attachUMOsRequest =
  (workspaceID, requestPromise) => ({
    type: actionTypes.TERM_ATTACH_UMOS_REQUEST,
    workspaceID,
    requestPromise,
  });

export const attachUMOsSuccess = workspaceID => ({
  type: actionTypes.TERM_ATTACH_UMOS_SUCCESS,
  workspaceID,
});

export const attachUMOsFailure = (workspaceID, err) => ({
  type: actionTypes.TERM_ATTACH_UMOS_FAILURE,
  workspaceID,
  err,
});

export const attachUMOs = (workspaceID, umoList, searchParams) =>
  function attachUMOsDispatch(dispatch) {
    const options = {
      OPTION: 'MAP_UMO',
      THES_ID: appConfig.thesaurus.id,
      TERM_ID: workspaceID,
      RETURN_TOTAL: 'TRUE',
    };
    if (searchParams) {
      options.SEARCH = _.isString(searchParams) ? searchParams : searchParams.toSearchQuery();
      options.EXCLUDE_UMO_ID = umoList;
    } else {
      options.UMO_ID = umoList;
    }

    if (appConfig.inProgressNotifications) {
      dispatch(addIntlInfo('highlightSetDialog.deleteWorkspace'));
    }

    const promise = piction.request('THESAURUS_TERM', options)
      .then((res) => {
        dispatch(editWorkspaceSuccess(workspaceID));
        dispatch(deselectAll());
        dispatch(addIntlInfo('navThesaurusPanel.attachSuccessful'));
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(editWorkspaceFailure(workspaceID, err));
        }
        return err;
      });

    dispatch(editWorkspaceRequest(workspaceID, promise));
    return promise;
  };



// WEBPACK FOOTER //
// ./src/js/actions/umo.js